import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'

import styles from './mainNavBar.module.scss'
import { useNavItems } from 'hooks/use-nav-items'
import { useNavData } from 'hooks/use-nav-data'
import { useNavUIState } from 'hooks/use-nav-ui-state'
import { useHeaderHeight } from 'hooks/use-header-height'
import { useScrollNavigation } from 'hooks/use-scroll-navigation'

import {
  selectAppReady,
  selectHasPendingMatches,
  selectUserIsLoggedIn,
  selectOwnProfile,
} from 'redux/selectors'

import { NavItem, MainNavBarProps } from './types'
import { LANGUAGE_HEADER_HEIGHT_FALLBACK } from './constants'

import MobileNavigation from './mobile-nav-bar/MobileNavigation'
import NavigationContent from './NavigationContent'
import JoinWTNBanner from './mobile-nav-bar/JoinWTNBanner'
import LanguageHeader from './LanguageHeader'
import { useMenuTranslations } from 'hooks/use-menu-translations'

const MainNavBar: React.FC<MainNavBarProps> = ({
  navItems: navItemsProp,
  mobileMenuItems: mobileMenuItemsProp,
  newsDropdownItems: newsDropdownItemsProp,
  showLanguageHeader,
  languageMessage: languageMessageProp,
  updateText: updateTextProp,
  currentLanguage: currentLanguageProp,
  languages: languagesProp,
  bannerText: bannerTextProp,
  enableSelfSubmission,
}) => {
  const { t } = useTranslation(['strCommonUI'])
  const [isMounted, setIsMounted] = useState(false)

  const {
    activeDropdown,
    activeMobileDropdown,
    languageHeaderHeight,
    isMobileView,
    isScrolled,
    isHeaderGroupVisible,
    isLanguageHeaderVisible,
    isMobileMenuOpen,
    setLanguageHeaderHeight,
    setIsScrolled,
    setIsHeaderGroupVisible,
    setIsLanguageHeaderVisible,
    toggleDropdown,
    openMobileSubmenu,
    closeMobileSubmenu,
    toggleMobileMenu,
    handleResize,
  } = useNavUIState()

  const mainNavRef = useRef<HTMLElement>(null)
  const languageHeaderRef = useRef<HTMLDivElement>(null)
  const joinBannerRef = useRef<HTMLDivElement>(null)
  const headerGroupWrapperRef = useRef<HTMLDivElement>(null)
  const mobileToggleRef = useRef<HTMLButtonElement>(null)

  const isLoggedIn = useSelector(selectUserIsLoggedIn)
  const userProfile = useSelector(selectOwnProfile)
  const appReady = useSelector(selectAppReady)
  const hasPendingMatches = useSelector(selectHasPendingMatches)

  const isEligibleForSelfSubmission = userProfile?.eligibleForSelfSubmission ?? false
  const canPostScore = isLoggedIn && isEligibleForSelfSubmission && (enableSelfSubmission ?? false)

  const {
    parsedNavItems,
    parsedMobileMenuItems,
    shouldShowLanguageHeaderState,
    setShouldShowLanguageHeaderState,
    languageHeaderMessage,
    headerCtaText,
    currentLanguageState,
    languagesList,
    bannerTextState,
    newsDropdownItemsState,
  } = useNavData({
    navItemsProp,
    mobileMenuItemsProp,
    newsDropdownItemsProp,
    showLanguageHeader,
    languageMessageProp,
    updateTextProp,
    currentLanguageProp,
    languagesProp,
    bannerTextProp,
  })

  const { desktopNavItems, mobileNavItems, dropdownRefs } = useNavItems({
    parsedNavItems,
    parsedMobileMenuItems,
    newsDropdownItemsState,
    effectiveIsLoggedIn: isLoggedIn,
    effectiveCanPostScore: canPostScore,
  })

  const { translatedAccountSubmenuItems } = useMenuTranslations()

  const shouldRenderLanguageHeader = shouldShowLanguageHeaderState && !!currentLanguageState
  const shouldRenderJoinBanner = isMobileView && !isLoggedIn

  const { updateHeaderTotalHeightVariable } = useHeaderHeight({
    isMounted,
    isMobileView,
    shouldRenderLanguageHeader,
    isLanguageHeaderVisible,
    shouldRenderJoinBanner,
    isHeaderGroupVisible,
    mainNavRef,
    languageHeaderRef,
    joinBannerRef,
  })

  useScrollNavigation({
    isMounted,
    isLanguageHeaderVisible,
    isHeaderGroupVisible,
    isMobileMenuOpen,
    setIsScrolled,
    setIsLanguageHeaderVisible,
    setIsHeaderGroupVisible,
    updateHeaderTotalHeight: updateHeaderTotalHeightVariable,
  })

  const activeMobileSubmenuItems = useMemo((): NavItem[] => {
    if (!activeMobileDropdown) return []
    const mapToNavItem = (item: any, index: number): NavItem => {
      const itemId =
        typeof item.id === 'string' && item.id
          ? item.id
          : `fallback-submenu-id-${activeMobileDropdown}-${index}`
      const itemName = typeof item.name === 'string' ? item.name : 'Unnamed Item'
      const itemPath = typeof item.path === 'string' ? item.path : '#'
      return { id: itemId, name: itemName, path: itemPath, hasDropdown: false, hasArrow: false }
    }
    let itemsToMap: Array<any> = []

    if (isLoggedIn && activeMobileDropdown === t('link.myAccount')) {
      itemsToMap = translatedAccountSubmenuItems
    } else if (activeMobileDropdown === t('link.news')) {
      itemsToMap = newsDropdownItemsState ?? []
    } else {
      const sourceItems: NavItem[] = parsedMobileMenuItems ?? []
      const activeItem = sourceItems.find(item => item.name === activeMobileDropdown)
      if (activeItem && activeItem.hasDropdown && Array.isArray(activeItem.dropdownItems)) {
        itemsToMap = activeItem.dropdownItems
      } else {
        itemsToMap = []
      }
    }
    if (!Array.isArray(itemsToMap)) {
      return []
    }
    return itemsToMap.map(mapToNavItem)
  }, [
    activeMobileDropdown,
    isLoggedIn,
    parsedMobileMenuItems,
    newsDropdownItemsState,
    translatedAccountSubmenuItems,
    t,
  ])

  useEffect(() => {
    setIsMounted(true)
    document.body.classList.add('has-fixed-navbar')
    return () => {
      document.body.classList.remove('has-fixed-navbar')
      document.documentElement.style.removeProperty('--header-total-height')
    }
  }, [])

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize)
      handleResize()
      return () => {
        window.removeEventListener('resize', handleResize)
      }
    }
  }, [handleResize])

  useEffect(() => {
    if (!isMounted) return

    if (!shouldRenderLanguageHeader) {
      if (languageHeaderHeight !== 0) {
        setLanguageHeaderHeight(0)
      }
      return
    }

    const measureLanguageHeader = () => {
      let currentLangHeaderHeight = 0
      if (languageHeaderRef.current) {
        try {
          currentLangHeaderHeight = languageHeaderRef.current.getBoundingClientRect().height
        } catch (error) {
          currentLangHeaderHeight = LANGUAGE_HEADER_HEIGHT_FALLBACK
        }
      } else {
        currentLangHeaderHeight = LANGUAGE_HEADER_HEIGHT_FALLBACK
      }
      if (currentLangHeaderHeight !== languageHeaderHeight) {
        setLanguageHeaderHeight(currentLangHeaderHeight)
      }
    }

    const timer = setTimeout(measureLanguageHeader, 150)
    measureLanguageHeader()

    return () => {
      clearTimeout(timer)
    }
  }, [
    isMounted,
    shouldRenderLanguageHeader,
    languageHeaderHeight,
    isMobileView,
    setLanguageHeaderHeight,
  ])

  useEffect(() => {
    if (isMounted) {
      updateHeaderTotalHeightVariable()
    }
  }, [
    isMounted,
    isMobileView,
    shouldRenderLanguageHeader,
    isLanguageHeaderVisible,
    languageHeaderHeight,
    shouldRenderJoinBanner,
    updateHeaderTotalHeightVariable,
  ])

  useEffect(() => {
    const clickOutside = (e: MouseEvent) => {
      if (activeDropdown) {
        const activeRef = dropdownRefs[activeDropdown]
        const toggleElement = document.querySelector(`[data-dropdown-toggle="${activeDropdown}"]`)
        if (
          activeRef?.current &&
          !activeRef.current.contains(e.target as Node) &&
          toggleElement &&
          !toggleElement.contains(e.target as Node)
        ) {
          toggleDropdown('')
        }
      }
    }
    document.addEventListener('mousedown', clickOutside)
    return () => document.removeEventListener('mousedown', clickOutside)
  }, [activeDropdown, dropdownRefs, toggleDropdown])

  const handleCloseLanguageHeader = useCallback(() => {
    setShouldShowLanguageHeaderState(false)
    if (typeof window !== 'undefined') {
      localStorage.setItem('languageHeaderClosed', 'true')
    }
  }, [setShouldShowLanguageHeaderState])

  const headerGroupTopOffset =
    isLanguageHeaderVisible && shouldRenderLanguageHeader ? languageHeaderHeight : 0

  // NOTE: For development mode, ensure appReady is true
  // or comment out this condition to make the navigation visible
  if (!isMounted || !appReady) {
    return <div className={styles.headerGroupWrapper}></div>
  }

  return (
    <>
      {shouldRenderLanguageHeader && (
        <LanguageHeader
          ref={languageHeaderRef}
          onClose={handleCloseLanguageHeader}
          isVisible={isLanguageHeaderVisible}
          message={languageHeaderMessage}
          updateText={headerCtaText}
          currentLanguage={currentLanguageState!}
          languages={languagesList}
        />
      )}

      <div
        ref={headerGroupWrapperRef}
        className={classNames(styles.headerGroupWrapper, {
          [styles.hidden]: !isHeaderGroupVisible && !isMobileMenuOpen,
        })}
        style={{
          top: `${headerGroupTopOffset}px`,
          transition: 'top 0.3s ease-out',
        }}
      >
        {shouldRenderJoinBanner && (
          <div ref={joinBannerRef}>
            <JoinWTNBanner bannerText={bannerTextState} />
          </div>
        )}

        <nav
          ref={mainNavRef}
          id='main-nav-header'
          className={classNames(styles.mainNav, {
            [styles.mainNavLifted]: isScrolled && !isMobileMenuOpen,
          })}
          aria-label={t('ariaLabel.mainNavigation', 'Main navigation')}
        >
          <NavigationContent
            isScrolled={isScrolled}
            isMobileMenuOpen={isMobileMenuOpen}
            desktopNavItems={desktopNavItems}
            activeDropdown={activeDropdown}
            toggleDropdown={toggleDropdown}
            dropdownRefs={dropdownRefs}
            isLoggedIn={isLoggedIn}
            userProfile={userProfile}
            canPostScore={canPostScore}
            hasPendingMatches={hasPendingMatches}
            toggleMobileMenu={toggleMobileMenu}
            mobileToggleRef={mobileToggleRef}
          />
        </nav>
      </div>

      <MobileNavigation
        isOpen={isMobileMenuOpen}
        onClose={toggleMobileMenu}
        navItems={mobileNavItems}
        activeSubmenu={activeMobileDropdown}
        onOpenSubmenu={openMobileSubmenu}
        onCloseSubmenu={closeMobileSubmenu}
        isLoggedIn={isLoggedIn}
        userProfile={userProfile}
        submenuItems={activeMobileSubmenuItems}
        hasPendingMatches={hasPendingMatches}
        triggerElementRef={mobileToggleRef}
      />
    </>
  )
}

export default MainNavBar
